import {
  PanelCommandDefinition,
  PanelCommandKey,
  PanelLoadings
} from "../interfaces/panel-alerts.interface";

/**
 * Definicion de todos los comandos del panel, en el orden en que se muestran.
 *
 * Sustituye a los arrays firsItems/secondItems y al switch por tipo de comando
 * que estaban repartidos entre el menu y sus botones: agregar o quitar un
 * comando es ahora agregar o quitar una fila de esta tabla.
 */
export const PANEL_COMMANDS: PanelCommandDefinition[] = [
  {
    key: "monitor",
    icon: "test-alarm.svg",
    label_key: "testing.alarm-test",
    endpoint: "status",
    socket_types: ["FEEDER_STATUS"],
    admin_only: false,
    hidden_for_alerts_only: false,
    always_idle: false
  },
  {
    key: "battery",
    icon: "bateria-blanco.svg",
    label_key: "testing.battery-test",
    endpoint: "battery",
    socket_types: ["BATTERY"],
    admin_only: true,
    hidden_for_alerts_only: false,
    always_idle: false
  },
  {
    key: "panel",
    icon: "panel-blanco.svg",
    label_key: "testing.panel-test",
    endpoint: "panel",
    iconWidth: "23px",
    iconHeight: "10px",
    socket_types: ["PANEL"],
    admin_only: true,
    hidden_for_alerts_only: false,
    always_idle: false
  },
  {
    key: "reset",
    icon: "reinicio-blanco.svg",
    label_key: "testing.alerts-reset",
    endpoint: "reset",
    socket_types: ["RESET"],
    admin_only: false,
    hidden_for_alerts_only: true,
    always_idle: false
  },
  {
    /* El vaciado tiene su propio componente y nunca deriva estado: por decision
       de producto el boton se queda siempre en gris. */
    key: "empty",
    icon: "dosificador.svg",
    label_key: "testing.empty-feeder",
    endpoint: "",
    socket_types: ["EMPTIED"],
    admin_only: false,
    hidden_for_alerts_only: true,
    always_idle: true
  },
  {
    key: "version",
    icon: "temporal-blanco.svg",
    label_key: "testing.request-version",
    endpoint: "version",
    socket_types: ["VERSION"],
    admin_only: true,
    hidden_for_alerts_only: false,
    always_idle: false
  },
  {
    key: "signal",
    icon: "senal-blanco.svg",
    label_key: "testing.request-signal",
    endpoint: "at/signal_request",
    iconWidth: "23px",
    iconHeight: "10px",
    socket_types: ["SIGNAL"],
    admin_only: true,
    hidden_for_alerts_only: false,
    always_idle: false
  },
  {
    key: "refeed",
    icon: "reenviar-blanco.svg",
    label_key: "testing.resend-ration",
    endpoint: "",
    socket_types: ["FEEDING_OK"],
    admin_only: false,
    hidden_for_alerts_only: true,
    always_idle: false
  },
  {
    key: "turn_off",
    icon: "power-off-blanco.svg",
    label_key: "testing.turn-off",
    endpoint: "turn_off",
    socket_types: ["OFF"],
    admin_only: false,
    hidden_for_alerts_only: true,
    always_idle: false,
    is_toggle: true
  },
  {
    key: "turn_on",
    icon: "power-blanco.svg",
    label_key: "testing.turn-on",
    endpoint: "turn_on",
    socket_types: ["ON"],
    admin_only: false,
    hidden_for_alerts_only: true,
    always_idle: false,
    is_toggle: true
  }
];

/**
 * Comandos que solo aplican a un subconjunto de equipos segun su estado
 * actual: encender solo va a los apagados y viceversa.
 */
export const PANEL_POWER_COMMANDS: PanelCommandKey[] = ["turn_on", "turn_off"];

/** Traduccion de tipo de trama del socket al comando que cierra. */
export const COMMAND_BY_SOCKET_TYPE: { [socketType: string]: PanelCommandKey } = {
  FEEDER_STATUS: "monitor",
  BATTERY: "battery",
  PANEL: "panel",
  RESET: "reset",
  EMPTIED: "empty",
  VERSION: "version",
  SIGNAL: "signal",
  FEEDING_OK: "refeed",
  ON: "turn_on",
  OFF: "turn_off"
};

/**
 * Traduccion del nombre de frame que reporta GET /v1/frame/ al comando del
 * panel. El backend nombra los frames por su tipo de trama, de modo que la
 * correspondencia coincide con la del socket; se mantiene como mapa propio
 * porque son dos contratos distintos y pueden divergir.
 */
export const COMMAND_BY_FRAME_NAME: { [frameName: string]: PanelCommandKey } = {
  ...COMMAND_BY_SOCKET_TYPE
};

/** Opciones del filtro de estado de la lista de equipos. */
export const PANEL_FILTER_STATUS_OPTIONS: Array<{ value: string; label_key: string }> = [
  { value: "", label_key: "general.all" },
  { value: "error", label_key: "filter.error" },
  { value: "done", label_key: "filter.done" },
  { value: "loading", label_key: "filter.loading" }
];

/**
 * Construye el objeto de estados de comando vacio para un alimentador.
 * Sustituye a las tres copias literales que habia repartidas por el modulo.
 * @returns Todos los comandos en estado "sin enviar".
 */
export function buildEmptyLoadings(): PanelLoadings {
  return {
    monitor: "",
    battery: "",
    panel: "",
    reset: "",
    empty: "",
    version: "",
    signal: "",
    refeed: "",
    turn_off: "",
    turn_on: "",
    turn_off_spin_time: "",
    turn_on_spin_time: ""
  };
}

/**
 * `status` de un registro de la cola de frames (`/v1/queue/`) que indica que ya
 * se envio al gateway; cualquier otro valor se considera en espera.
 */
export const PANEL_QUEUE_SENT_STATUS = 2;

/** Tipos de trama XBee de transmision (0x10 y 0x11) que llevan la mac destino. */
export const XBEE_TRANSMIT_FRAME_TYPES = [0x10, 0x11];

/** Cadencia de consulta de la cola mientras haya equipos esperando respuesta. */
export const PANEL_QUEUE_POLL_MS = 5000;

/**
 * Registros por pagina al leer la cola, que el backend devuelve del mas viejo al
 * mas nuevo. El backend no entrega mas de 100 por pagina aunque se pidan mas.
 */
export const PANEL_QUEUE_PAGE_SIZE = 100;
